/**
 * form-outbox.ts
 * Field forms saved with no signal (operator-offline-forms S6): combustible,
 * falla, viaje and ausencia. A form that cannot reach the server is kept on
 * the phone (form-store.ts) and sent on its own when the connection returns;
 * Hoy lists it as waiting or rejected (retry, discard), same posture as the
 * dictation queue (dictation-queue.ts).
 *
 * Safe to resend (S3): every form carries a clientRequestId generated once
 * when the form is opened; the backend answers a resend with the record it
 * already created. A falla is two creates — the informe, then the falla under
 * it — each with its own key, so a pass cut between them never opens a
 * second informe.
 *
 * Time (S2): a queued form sends capturedAt, the moment the worker tapped
 * save; the server records it as the business time (bounded to 72 h back).
 * A form that goes out right away sends no capturedAt: the server time is it.
 *
 * Send outcomes, per form, in the order they were filled:
 *   - sent → forgotten (it now lives on the server and shows in its list).
 *   - no connection, lapsed session (401) or a passing server problem
 *     (408, 429, 5xx) → stays 'waiting'; the pass stops there.
 *   - any other rejection → 'failed' with a short reason; Hoy offers retry
 *     or discard.
 */
import { ref } from 'vue';
import { CAPTURED_AT_OUT_OF_RANGE } from '@suite/contracts-kernel';
import { SERVICIOS_ERROR_CODES } from '@suite/contracts-servicios';
import { createFailure, createFuelLoad, createReport, createTrip, requestAbsence } from './api.js';
import { ApiError, isUnreachable } from './http.js';
import { currentClaims } from './session.js';
import { indexedDbFormStore, type FormStore, type QueuedForm, type QueuedFormPayload } from './form-store.js';

export type { QueuedForm, QueuedFormKind, QueuedFormPayload } from './form-store.js';

/** The current user's forms on this phone, oldest first. */
export const queuedForms = ref<QueuedForm[]>([]);

let store: FormStore = indexedDbFormStore;
let sending: Promise<void> | null = null;

/** Swap the backing store (tests) and start from an empty view of it. */
export function useFormStore(next: FormStore): void {
  store = next;
  sending = null;
  queuedForms.value = [];
}

function currentOwner(): string | null {
  const claims = currentClaims.value;
  return claims?.scope === 'tenant' ? `${claims.tenantId}:${claims.sub}` : null;
}

async function ownForms(): Promise<QueuedForm[]> {
  const owner = currentOwner();
  if (!owner) return [];
  const rows = await store.all();
  return rows.filter((r) => r.owner === owner).sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
}

export async function refreshForms(): Promise<void> {
  try {
    queuedForms.value = await ownForms();
  } catch {
    // Storage unavailable: nothing can be kept on this phone either.
    queuedForms.value = [];
  }
}

/** One form as the worker filled it. `capturedAt` is set once, at the first save. */
export interface FilledForm {
  readonly clientRequestId: string;
  readonly capturedAt: string;
  readonly summary: string;
  readonly payload: QueuedFormPayload;
}

/**
 * Send one form. Returns the id of the record the worker lands on (the
 * falla, not its informe). `withCapturedAt` is false for a form going out
 * right away (the server time is the capture time).
 */
async function deliver(form: FilledForm, withCapturedAt: boolean): Promise<string> {
  const time = withCapturedAt ? { capturedAt: form.capturedAt } : {};
  const { payload, clientRequestId } = form;
  switch (payload.kind) {
    case 'fuel-load':
      return (await createFuelLoad({ ...payload.body, clientRequestId, ...time })).id;
    case 'trip':
      return (await createTrip({ ...payload.body, clientRequestId, ...time })).id;
    case 'absence':
      return (await requestAbsence({ ...payload.body, clientRequestId })).id;
    case 'failure': {
      const report = await createReport({ ...payload.report, clientRequestId: payload.reportClientRequestId, ...time });
      return (await createFailure({ ...payload.failure, reportId: report.id, clientRequestId })).id;
    }
  }
}

/** Worth waiting out: no connection, a lapsed session, or a passing server problem. */
function isStillOffline(err: unknown): boolean {
  return isUnreachable(err) || (err instanceof ApiError && err.status === 401);
}

/** The worker-facing reason a queued form was rejected. */
export function rejectionReason(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === CAPTURED_AT_OUT_OF_RANGE) {
      return err.fieldErrors.some((f) => f.message === 'future')
        ? 'La hora del teléfono está adelantada. Corregila y reintentá.'
        : 'Pasaron más de 72 h desde que la cargaste: ya no se puede enviar.';
    }
    if (err.code === SERVICIOS_ERROR_CODES.METER_READING_REASON_REQUIRED) {
      return 'Falta la lectura de horómetro/kilómetros. Descartala y cargala de nuevo con el motivo.';
    }
    return err.message;
  }
  return 'No se pudo enviar.';
}

export type SubmitOutcome = { readonly status: 'sent'; readonly id: string } | { readonly status: 'queued' };

/**
 * Save button of a form: send it now; with no signal (or a passing server
 * problem) keep it on the phone to go out on its own. A real rejection (a
 * 4xx) is thrown untouched so the form shows it, exactly as before.
 */
export async function submitForm(form: FilledForm): Promise<SubmitOutcome> {
  try {
    return { status: 'sent', id: await deliver(form, false) };
  } catch (err) {
    if (!isUnreachable(err)) throw err;
    const owner = currentOwner();
    if (!owner) throw err;
    await store.put({ ...form, owner, state: 'waiting' });
    await refreshForms();
    return { status: 'queued' };
  }
}

/** Write a send result back only if the worker did not discard the form while it was in flight. */
async function putIfStillQueued(row: QueuedForm): Promise<void> {
  const rows = await store.all();
  if (rows.some((r) => r.clientRequestId === row.clientRequestId)) await store.put(row);
}

async function sendOne(row: QueuedForm): Promise<'continue' | 'stop'> {
  try {
    await deliver(row, true);
    await store.remove(row.clientRequestId);
    return 'continue';
  } catch (err) {
    if (isStillOffline(err)) return 'stop';
    await putIfStillQueued({ ...row, state: 'failed', error: rejectionReason(err) });
    return 'continue';
  }
}

async function runSendPass(): Promise<void> {
  const owner = currentOwner();
  for (const row of await ownForms()) {
    // Someone else signed in mid-pass: never send A's form with B's cookie.
    if (currentOwner() !== owner) break;
    if (row.state !== 'waiting') continue;
    if ((await sendOne(row)) === 'stop') break;
  }
}

/** Send every waiting form of the current user. One pass at a time. */
export async function sendQueuedForms(): Promise<void> {
  sending ??= runSendPass()
    .catch(() => undefined)
    .finally(() => {
      sending = null;
    });
  await sending;
  await refreshForms();
}

export async function retryForm(clientRequestId: string): Promise<void> {
  const row = (await ownForms()).find((r) => r.clientRequestId === clientRequestId);
  if (!row) return;
  // Rebuilt field by field so the stale error is not carried over.
  const { owner, capturedAt, summary, payload } = row;
  await store.put({ clientRequestId, owner, capturedAt, summary, payload, state: 'waiting' });
  await sendQueuedForms();
}

export async function discardForm(clientRequestId: string): Promise<void> {
  await store.remove(clientRequestId);
  await refreshForms();
}

const RETRY_EVERY_MS = 60_000;

/**
 * Keep trying in the background: on start, when the connection is back,
 * when the app returns to the foreground, and every minute while something
 * is still waiting — same triggers as the dictation queue.
 */
export function startFormSync(): void {
  const trigger = (): void => {
    void sendQueuedForms();
  };
  trigger();
  window.addEventListener('online', trigger);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') trigger();
  });
  setInterval(() => {
    if (queuedForms.value.some((f) => f.state === 'waiting')) trigger();
  }, RETRY_EVERY_MS);
}

/** A new form identity: generate when the form opens, keep across save taps. */
export function newClientRequestId(): string {
  return crypto.randomUUID();
}
